import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { BillStatus } from '@/types';
import { useI18n } from '@/hooks/useI18n';

interface StatusUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (status: BillStatus) => Promise<void>;
  currentStatus: BillStatus;
  isLoading?: boolean;
  error?: string | null;
}

const StatusUpdateDialog: React.FC<StatusUpdateDialogProps> = ({
  open,
  onClose,
  onConfirm,
  currentStatus,
  isLoading = false,
  error = null
}) => {
  const { t } = useI18n();
  const [newStatus, setNewStatus] = React.useState(currentStatus);

  React.useEffect(() => {
    setNewStatus(currentStatus);
  }, [currentStatus, open]);

  const handleConfirm = async () => {
    await onConfirm(newStatus);
  };

  const statusOptions = [
    { value: BillStatus.DRAFT, label: t('bill.status.draft') },
    { value: BillStatus.GENERATED, label: t('bill.status.generated') },
    { value: BillStatus.SENT, label: t('bill.status.sent') },
    { value: BillStatus.PARTIALLY_PAID, label: t('bill.status.partiallyPaid') },
    { value: BillStatus.FULLY_PAID, label: t('bill.status.fullyPaid') },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('bill.persistent.updateStatus')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            select
            fullWidth
            label={t('bill.persistent.status')}
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value as BillStatus)}
            disabled={isLoading}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading || newStatus === currentStatus}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {t('common.update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusUpdateDialog;